import React, { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiCheck } from 'react-icons/fi';

// How much mail the first sync brings in (Focused Inbox and Sent Items).
// After connecting, new mail always arrives live; the backend validates the
// value (appAuthRouter.mail_history_days).
const CHOICES = [
  {
    days: null,
    label: 'All mail',
    hint: 'Your whole Focused Inbox and Sent Items',
  },
  { days: 90, label: 'Last 90 days', hint: 'Recent mail only' },
  { days: 30, label: 'Last 30 days', hint: 'Quickest start with some history' },
  { days: 0, label: 'Only new mail', hint: 'Nothing before today' },
];

const selectedRing = (on) =>
  on
    ? 'border-[#5652CF] bg-[#5652CF]/10'
    : 'border-zinc-700 hover:border-zinc-500';

const OutlookSyncModal = ({ onContinue, onClose }) => {
  const [days, setDays] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div
        role="dialog"
        aria-labelledby="outlook-sync-title"
        className="bg-1 flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-xl border border-zinc-700 p-6"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/icons/outlook.png"
              alt=""
              className="h-9 w-9 rounded-lg bg-zinc-200 p-1.5"
            />
            <div>
              <h2
                id="outlook-sync-title"
                className="text-lg font-semibold text-zinc-100"
              >
                Connect Outlook
              </h2>
              <p className="text-xs text-zinc-400">
                Choose how much of your existing mail to bring in.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <IoMdClose size={20} />
          </button>
        </div>

        <section className="mt-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
            Sync
          </h3>
          <div
            role="radiogroup"
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {CHOICES.map(({ days: value, label, hint }) => {
              const on = days === value;
              return (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setDays(value)}
                  className={`relative rounded-lg border p-3 text-left transition-colors ${selectedRing(on)}`}
                >
                  <span className="block text-sm font-medium text-zinc-100">
                    {label}
                  </span>
                  <span className="block text-xs text-zinc-400">{hint}</span>
                  {on && (
                    <FiCheck
                      className="absolute right-2 top-2 text-[#9b98f5]"
                      size={14}
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-zinc-400">
            New mail arrives live after you connect, whatever you choose. Each
            synced mail is summarised by AI, so more history takes longer to
            appear.
          </p>
        </section>

        <div className="mt-6 flex justify-end gap-2 border-t border-zinc-800 pt-4">
          <button
            onClick={onClose}
            className="rounded-md border border-zinc-600 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onContinue({ history_days: days })}
            className="rounded-md bg-[#5652CF] px-4 py-2 text-sm font-medium text-white hover:bg-[#4642bf]"
          >
            Continue to Microsoft
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutlookSyncModal;
